"use client";

import {
  BaseClickable,
  Body,
  type ButtonVariant,
  ConfirmationDialog,
} from "@biom3/react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import { Modal } from "@/components/modal";
import { ImportVsLinkInfo } from "@/components/wallets/ImportVsLinkInfo";
import { MODAL_HEIGHT, MODAL_WIDTH } from "@/constants";
import type {
  LinkWalletCallback,
  LinkWalletError,
  LinkWalletModal as LinkWalletModalType,
  LinkWalletState,
} from "@/types";
import { analytics } from "@analytics";
import { useMemo } from "react";

type LinkWalletModalParams = {
  state?: LinkWalletState;
  error?: LinkWalletError;
  onConfirm?: LinkWalletCallback;
  onLinkAnother?: LinkWalletCallback;
  onAfterClose?: LinkWalletCallback;
  onAfterRetry?: LinkWalletCallback;
};

type LinkWalletModalImage = {
  src: string;
  altKey: string;
};
type LinkWalletModalButton = {
  variant: ButtonVariant;
  textKey: string;
  action: () => void;
};
type LinkWalletModalState = {
  image: LinkWalletModalImage;
  titleKey: string;
  descriptionKey: string | undefined;
  appendNoSmartContracts: boolean;
  primaryButton: LinkWalletModalButton | undefined;
  secondaryButton: LinkWalletModalButton | undefined;
};

const linkWalletTranslations: Record<string, string> = {
  link_wallet_failed: "We failed to link the wallet.",
  link_wallet_failed_heading: "Could not link your wallet",
  link_wallet_failed_max_wallets_heading:
    "You've reached the maximum number of linked wallets",
  link_wallet_failed_already_linked_heading:
    "This wallet has already been linked",
  link_wallet_failed_already_linked:
    "If your wallet has multiple accounts, ensure you're connecting to the right account.",
  link_wallet_failed_connect_failed:
    "We tried to call the wallet and it could not connect.",
  link_wallet_failed_unverified:
    "We tried to call the wallet and we could not verify the signature.",
  link_wallet_failed_network_failed_heading: "Switch to the Ethereum network",
  link_wallet_failed_network_failed:
    "Your wallet is currently connected to another network. Please switch networks to link your wallet.",
  waiting_for_wallet_link: "Waiting for you to link your wallet",
  approve_connection_request:
    "Approve the connection request to link your wallet",
  linking_wallet: "Linking wallet",
  link_wallet_success_title: "Wallet linked",
  import_option_modal_title:
    "Now, let's import your collectables to use them in game",
  link_wallet_success_empty_title: "No zkEVM collectibles found in this wallet",
  link_wallet_success_empty_description:
    "Once you add Immutable X and Immutable zkEVM game collectibles to your wallet, they will show up on your inventory as view-only. Only zkEVM collectibles can be imported into your Passport wallet.",
  link_wallet_success_empty_link_another_cta: "Link another wallet",
  close: "Close",
  retry: "Try Again",
  retry_after_swtich_network: "I've switched, try again",
  ok: "OK",
  no_leave_items: "No, leave items",
};

const getLinkWalletText = (key: string): string =>
  linkWalletTranslations[key] || key;

const LinkFailedImage = {
  altKey: "link_wallet_failed",
  src: "/images/wallets/link_failed.svg",
};

const DefaultLinkWalletContent = {
  descriptionKey: undefined,
  primaryButton: undefined,
  secondaryButton: undefined,
  appendNoSmartContracts: false,
};

export const LinkWalletModal = ({
  error,
  state,
  onAfterClose,
  onAfterRetry,
  onConfirm,
  onLinkAnother,
}: LinkWalletModalParams) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasTimedOut, setHasTimedOut] = useState<boolean>(false);
  const [isImportVsLinkOpen, setIsImportVsLinkOpen] = useState<boolean>(false);

  const analyticsParams = useMemo(
    () => ({
      userJourney: "Wallets",
      screen: "LinkWallet",
    }),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const open = state !== undefined || error !== undefined;
    if (isModalOpen !== open) {
      setHasTimedOut(false);
    }
    setIsModalOpen(open);
  }, [state, error]);

  useEffect(() => {
    if (state === "WAITING") {
      const timeout = setTimeout(() => {
        setHasTimedOut(true);
        analytics.track(
          "PassportDashboardWalletsLinkWallet_LinkWalletWaitingTimeoutEffectStarted",
        );
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [state]);

  const onRetry = useCallback(() => {
    analytics.track(
      "PassportDashboardWalletsLinkWallet_RetryLinkWalletErrorButtonPressed",
    );
    if (onAfterRetry) onAfterRetry();
  }, [onAfterRetry]);

  const onClose = useCallback(async () => {
    analytics.track("PassportDashboardWalletsLinkWallet_CloseButtonPressed");
    if (onAfterClose) onAfterClose();
    setIsModalOpen(false);
  }, [onAfterClose]);

  const closeButton = useMemo(
    () => ({
      variant: "tertiary" as const,
      textKey: "close",
      action: onClose,
    }),
    [onClose],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const retryButton = useMemo(
    () => ({
      variant: "primary" as const,
      textKey: "retry",
      action: onRetry,
    }),
    [onClose],
  );

  const component: LinkWalletModalType = <></>;
  let content: LinkWalletModalState | undefined = undefined;
  if (error && !state) {
    if (error?.code === 4001) {
      // If the user rejected the connection request just close the modal
      return component;
    }

    switch (error?.code) {
      case "MAX_WALLETS_LINKED":
        content = {
          ...DefaultLinkWalletContent,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_max_wallets_heading",
          primaryButton: closeButton,
        };
        break;
      case "ALREADY_LINKED":
        content = {
          ...DefaultLinkWalletContent,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_already_linked_heading",
          descriptionKey: "link_wallet_failed_already_linked",
          primaryButton: closeButton,
        };
        break;
      case "CONNECT_FAILED":
        content = {
          appendNoSmartContracts: true,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_heading",
          descriptionKey: "link_wallet_failed_connect_failed",
          primaryButton: retryButton,
          secondaryButton: closeButton,
        };
        break;
      case "SIGNATURE_UNVERIFIED":
        content = {
          appendNoSmartContracts: true,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_heading",
          descriptionKey: "link_wallet_failed_unverified",
          primaryButton: retryButton,
          secondaryButton: closeButton,
        };
        break;
      case "UNAUTHORISED":
        content = {
          ...DefaultLinkWalletContent,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_heading",
          descriptionKey: "link_wallet_failed",
          primaryButton: closeButton,
        };
        break;
      case "NETWORK_FAILED":
        content = {
          ...DefaultLinkWalletContent,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_network_failed_heading",
          descriptionKey: "link_wallet_failed_network_failed",
          primaryButton: {
            ...retryButton,
            textKey: "retry_after_swtich_network",
          },
          secondaryButton: closeButton,
        };
        break;
      default:
        content = {
          ...DefaultLinkWalletContent,
          image: LinkFailedImage,
          titleKey: "link_wallet_failed_heading",
          descriptionKey: "link_wallet_failed",
          primaryButton: closeButton,
        };
        break;
    }
  } else {
    switch (state) {
      case "WAITING":
        content = {
          ...DefaultLinkWalletContent,
          image: {
            src: "/images/waitingWalletLinkingModal.svg",
            altKey: "waiting_for_wallet_link",
          },
          titleKey: "waiting_for_wallet_link",
          descriptionKey: "approve_connection_request",
          primaryButton: hasTimedOut ? closeButton : undefined,
        };
        break;
      case "LINKING":
        content = {
          ...DefaultLinkWalletContent,
          image: {
            src: "/images/linkWalletLinkingModal.svg",
            altKey: "linking_wallet",
          },
          titleKey: "linking_wallet",
        };
        break;
      case "SUCCESS":
        content = {
          ...DefaultLinkWalletContent,
          image: {
            src: "/images/linkSuccess.svg",
            altKey: "link_wallet_success_title",
          },
          titleKey: "link_wallet_success_title",
        };
        break;
      case "IMPORT":
        content = {
          ...DefaultLinkWalletContent,
          image: {
            src: "/images/importOption.svg",
            altKey: "import_option_modal_title",
          },
          titleKey: "import_option_modal_title",
          primaryButton: {
            variant: "primary",
            textKey: "ok",
            action: () => {
              analytics.track(
                "PassportDashboardWalletsImportPrompt_OkButtonPressed",
              );
              if (onConfirm) {
                onConfirm();
              }
            },
          },
          secondaryButton: {
            ...closeButton,
            textKey: "no_leave_items",
          },
        };
        break;
      case "EMPTY_WALLET":
        content = {
          ...DefaultLinkWalletContent,
          image: {
            src: "/images/linkSuccesNoCollections.svg",
            altKey: "link_wallet_success_empty_title",
          },
          titleKey: "link_wallet_success_empty_title",
          descriptionKey: "link_wallet_success_empty_description",
          primaryButton: onLinkAnother
            ? {
                variant: "tertiary",
                textKey: "link_wallet_success_empty_link_another_cta",
                action: () => {
                  analytics.track(
                    "PassportDashboardWalletsEmptyLink_LinkAnotherButtonPressed",
                  );
                  if (onLinkAnother) {
                    onLinkAnother();
                  }
                },
              }
            : undefined,
          secondaryButton: {
            ...closeButton,
          },
        };
        break;
    }
  }

  if (!content) {
    return <></>;
  }

  return (
    <>
      <ConfirmationDialog visible={isModalOpen} outsideClicksClose={false}>
        <ConfirmationDialog.Content
          testId="LinkWalletModal"
          sx={{
            width: MODAL_WIDTH,
            height: MODAL_HEIGHT,
            alignItems: "center",
          }}
        >
          <ConfirmationDialog.Content.Image
            alt={getLinkWalletText(content.image.altKey)}
            use={
              <Image
                src={content.image.src}
                width={430}
                height={240}
                alt={getLinkWalletText(content.image.altKey)}
              />
            }
          />
          <ConfirmationDialog.Content.Title testId="LinkWalletModal-Title">
            {getLinkWalletText(content.titleKey)}
          </ConfirmationDialog.Content.Title>
          {content.descriptionKey && (
            <ConfirmationDialog.Content.Caption testId="LinkWalletModal-Description">
              {getLinkWalletText(content.descriptionKey)}
              {content.appendNoSmartContracts
                ? " If you're using a smart contract wallet (not EOA) it's not supported."
                : ""}
            </ConfirmationDialog.Content.Caption>
          )}
          {state === "IMPORT" && (
            <ConfirmationDialog.Content.Caption>
              To play, craft and trade your in-game items on Passport, import
              them below.{" "}
              <Body
                sx={{
                  color: "base.color.text.link.primary",
                  textDecoration: "none",
                }}
                rc={
                  <BaseClickable
                    onClick={() => {
                      setIsImportVsLinkOpen(true);
                    }}
                  />
                }
              >
                Learn more
              </Body>
            </ConfirmationDialog.Content.Caption>
          )}
          {content.primaryButton && (
            <ConfirmationDialog.Content.Button
              testId="LinkWalletModal-PrimaryButton"
              size="large"
              variant={content.primaryButton.variant}
              onClick={content.primaryButton.action}
            >
              {getLinkWalletText(content.primaryButton.textKey)}
            </ConfirmationDialog.Content.Button>
          )}
          {content.secondaryButton && (
            <ConfirmationDialog.Content.Button
              testId="LinkWalletModal-SecondaryButton"
              size="large"
              variant={content.secondaryButton.variant}
              onClick={content.secondaryButton.action}
            >
              {getLinkWalletText(content.secondaryButton.textKey)}
            </ConfirmationDialog.Content.Button>
          )}
        </ConfirmationDialog.Content>
      </ConfirmationDialog>
      <Modal open={isImportVsLinkOpen}>
        <Modal.Content>
          <ImportVsLinkInfo
            onBack={undefined}
            onClose={() => {
              setIsImportVsLinkOpen(false);
            }}
          />
        </Modal.Content>
      </Modal>
    </>
  );
};
